import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Switch } from '../../ui/components';
import { useCommercialSettings } from '../../app/PropertyContext';
import { useT } from '../../i18n';

const EMPTY = {
  supplierId: '',
  supplierSku: '',
  description: '',
  purchaseUomId: '',
  packSize: 1,
  priceAmount: '',
  priceCurrency: '',
  minimumOrderQuantity: '',
  leadTimeDays: '',
  isPreferred: false,
  isActive: true,
};

function fromLink(l, defaultCurrency) {
  if (!l) return { ...EMPTY, priceCurrency: defaultCurrency };
  return {
    supplierId: l.supplierId ?? '',
    supplierSku: l.supplierSku ?? '',
    description: l.description ?? '',
    purchaseUomId: l.purchaseUomId ?? '',
    packSize: l.packSize ?? 1,
    priceAmount: l.price?.amount ?? '',
    priceCurrency: l.price?.currency ?? defaultCurrency,
    minimumOrderQuantity: l.minimumOrderQuantity ?? '',
    leadTimeDays: l.leadTimeDays ?? '',
    isPreferred: l.isPreferred ?? false,
    isActive: l.isActive ?? true,
  };
}

export default function SupplierItemForm({ link, suppliers = [], units = [], submitting, onSubmit, onCancel }) {
  const t = useT();
  const { currencies, defaultCurrency } = useCommercialSettings();
  const [form, setForm] = useState(() => fromLink(link, defaultCurrency));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(fromLink(link, defaultCurrency));
    setErrors({});
  }, [link, defaultCurrency]);

  const currencyOptions = useMemo(() => {
    const codes = [...currencies];
    if (form.priceCurrency && !codes.includes(form.priceCurrency)) codes.push(form.priceCurrency);
    return codes.map((c) => ({ value: c, label: c }));
  }, [currencies, form.priceCurrency]);

  const supplierOptions = useMemo(
    () => [{ value: '', label: t('— Select supplier —') }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))],
    [suppliers, t],
  );
  const unitOptions = useMemo(
    () => [{ value: '', label: t('— No unit —') }, ...units.map((u) => ({ value: u.id, label: `${u.name} (${u.code})` }))],
    [units, t],
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.supplierId) next.supplierId = t('Choose a supplier.');
    if (!(Number(form.packSize) > 0)) next.packSize = t('Pack size must be greater than 0.');
    if (form.priceAmount !== '' && Number(form.priceAmount) < 0) next.priceAmount = t('Must be ≥ 0.');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      supplierId: form.supplierId,
      supplierSku: form.supplierSku.trim() || null,
      description: form.description.trim() || null,
      purchaseUomId: form.purchaseUomId || null,
      packSize: Number(form.packSize) || 1,
      price: form.priceAmount === '' ? null : { amount: Number(form.priceAmount), currency: (form.priceCurrency || defaultCurrency).toUpperCase() },
      minimumOrderQuantity: form.minimumOrderQuantity === '' ? null : Number(form.minimumOrderQuantity),
      leadTimeDays: form.leadTimeDays === '' ? null : Number(form.leadTimeDays),
      isPreferred: form.isPreferred,
      isActive: form.isActive,
    });
  };

  return (
    <form className="pp-form" onSubmit={handleSubmit}>
      <div className="ps-field">
        <span className="ps-field__label">{t('Supplier')}</span>
        <Select
          value={form.supplierId}
          onChange={(v) => setForm((f) => ({ ...f, supplierId: v }))}
          options={supplierOptions}
          ariaLabel={t('Supplier')}
          disabled={!!link}
          searchable
        />
        {errors.supplierId && <span className="ps-field__error">{errors.supplierId}</span>}
      </div>

      <div className="pp-form__row">
        <Input label={t('Supplier SKU')} placeholder={t('Vendor reference')} value={form.supplierSku} onChange={set('supplierSku')} />
        <Input label={t('Description')} placeholder={t('As listed by supplier')} value={form.description} onChange={set('description')} />
      </div>

      <div className="pp-form__row">
        <div className="ps-field">
          <span className="ps-field__label">{t('Purchase unit')}</span>
          <Select value={form.purchaseUomId} onChange={(v) => setForm((f) => ({ ...f, purchaseUomId: v }))} options={unitOptions} ariaLabel={t('Purchase unit')} searchable />
        </div>
        <Input label={t('Pack size')} type="number" hint={t('Stock units per purchase unit (e.g. 24).')} value={form.packSize} onChange={set('packSize')} error={errors.packSize} />
      </div>

      <div className="pp-form__row">
        <Input label={t('Price (per purchase unit)')} type="number" placeholder="0.00" value={form.priceAmount} onChange={set('priceAmount')} error={errors.priceAmount} />
        <div className="ps-field">
          <span className="ps-field__label">{t('Currency')}</span>
          <Select value={form.priceCurrency} onChange={(v) => setForm((f) => ({ ...f, priceCurrency: v }))} options={currencyOptions} ariaLabel={t('Currency')} />
        </div>
      </div>

      <div className="pp-form__row">
        <Input label={t('Min. order qty')} type="number" value={form.minimumOrderQuantity} onChange={set('minimumOrderQuantity')} />
        <Input label={t('Lead time (days)')} type="number" value={form.leadTimeDays} onChange={set('leadTimeDays')} />
      </div>

      <Switch checked={form.isPreferred} onChange={(v) => setForm((f) => ({ ...f, isPreferred: v }))} label={t('Preferred supplier for this item')} />
      <Switch checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label={t('Active')} />

      <div className="pp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{link ? t('Save changes') : t('Add supplier')}</Button>
      </div>
    </form>
  );
}
